import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Chip, CircularProgress, Grid, Paper, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { lmsService } from '../../services/api';

function Stat({ title, value, hint }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 120,
        }}
      >
        <Typography variant="body2" color="text.secondary">{title}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 'auto', minHeight: 18 }}>
          {hint || '\u00a0'}
        </Typography>
      </CardContent>
    </Card>
  );
}

function fmt(dt) {
  if (!dt) return '—';
  try {
    return new Date(dt).toLocaleString('uk-UA');
  } catch {
    return '—';
  }
}

function ts(dt) {
  if (!dt) return 0;
  const n = new Date(dt).getTime();
  return Number.isFinite(n) ? n : 0;
}

function canManageLms() {
  try {
    const u = JSON.parse(localStorage.getItem('currentUser') || 'null');
    const p = u?.effective_permissions || u?.role?.permissions || {};
    return p['menu.lms.manage'] === true;
  } catch {
    return false;
  }
}

function buildActivity(progress, attempts) {
  const rows = [];
  for (const row of progress || []) {
    rows.push({
      key: `c-${row.id}`,
      kind: 'course',
      staff_name: row.staff_name,
      title: row.course_title,
      result: row.status === 'done' ? 'завершено' : 'у процесі',
      resultColor: row.status === 'done' ? 'success' : 'default',
      detail: '',
      at: row.completed_at || row.opened_at,
    });
  }
  for (const a of attempts || []) {
    let result = `${a.score}/${a.total} (${a.pct}%)`;
    let resultColor = 'default';
    if (a.passed === true) {
      result += ' · пройдено';
      resultColor = 'success';
    } else if (a.passed === false) {
      result += ' · ні';
      resultColor = 'warning';
    }
    rows.push({
      key: `q-${a.id}`,
      kind: 'quiz',
      staff_name: a.staff_name,
      title: a.quiz_title,
      result,
      resultColor,
      detail: a.attempt_no ? `#${a.attempt_no}` : '',
      at: a.created_at,
    });
  }
  rows.sort((a, b) => ts(b.at) - ts(a.at));
  return rows;
}

function passChip(passed) {
  if (passed === true) return <Chip size="small" color="success" label="пройдено" />;
  if (passed === false) return <Chip size="small" color="warning" label="не пройдено" />;
  return <Chip size="small" label="—" />;
}

export default function LmsResultsPage() {
  const manage = useMemo(() => canManageLms(), []);
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        if (manage) {
          const res = await lmsService.dashboard();
          setData(res.data);
          setAttempts(res.data?.recent_attempts || []);
        } else {
          const res = await lmsService.attempts();
          setAttempts(Array.isArray(res.data) ? res.data : res.data?.results || []);
          setData(null);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [manage]);

  const activity = useMemo(() => {
    if (manage) return buildActivity(data?.recent_progress || [], attempts);
    return buildActivity([], attempts);
  }, [manage, data, attempts]);

  const byStaff = data?.by_staff || [];
  const byRole = data?.by_role || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AssessmentIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {manage ? 'Дашборд' : 'Мої результати'}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          {manage && data ? (
            <Grid container spacing={2} alignItems="stretch" sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                <Box sx={{ width: '100%' }}>
                  <Stat
                    title="Курсів"
                    value={data?.courses?.total ?? 0}
                    hint={`опубліковано: ${data?.courses?.published ?? 0}`}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                <Box sx={{ width: '100%' }}>
                  <Stat
                    title="Завершень курсів"
                    value={data?.courses?.done_progress ?? 0}
                    hint={`у процесі: ${data?.courses?.in_progress ?? 0}`}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                <Box sx={{ width: '100%' }}>
                  <Stat
                    title="Тестів"
                    value={data?.quizzes?.total ?? 0}
                    hint={`опубліковано: ${data?.quizzes?.published ?? 0}`}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                <Box sx={{ width: '100%' }}>
                  <Stat
                    title="Спроб тестів"
                    value={data?.quizzes?.attempts ?? 0}
                    hint={`середній %: ${data?.quizzes?.avg_pct ?? 0}`}
                  />
                </Box>
              </Grid>
            </Grid>
          ) : null}

          {manage ? (
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="По працівниках" />
              <Tab label="По відділах (ролях)" />
              <Tab label="Активність" />
            </Tabs>
          ) : null}

          {manage && tab === 0 ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Найкращий результат по кожному працівнику × тесту (для звітності / ЗП).
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Працівник</TableCell>
                      <TableCell>Відділ (роль)</TableCell>
                      <TableCell>Тест</TableCell>
                      <TableCell align="right">%</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell align="right">Спроб</TableCell>
                      <TableCell>Остання</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byStaff.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography color="text.secondary">Ще немає спроб тестів.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : byStaff.map((row, idx) => (
                      <TableRow key={`${row.staff_id}-${row.quiz_id}-${idx}`} hover>
                        <TableCell>{row.staff_name}</TableCell>
                        <TableCell>{row.role_name || '—'}</TableCell>
                        <TableCell>{row.quiz_title}</TableCell>
                        <TableCell align="right">{row.pct ?? '—'}</TableCell>
                        <TableCell>{passChip(row.passed)}</TableCell>
                        <TableCell align="right">{row.attempts_count}</TableCell>
                        <TableCell>{fmt(row.last_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}

          {manage && tab === 1 ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Агрегат по ролях персоналу (у системі ролі = відділи).
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Відділ (роль)</TableCell>
                      <TableCell align="right">Людей</TableCell>
                      <TableCell align="right">Результатів</TableCell>
                      <TableCell align="right">Пройшли</TableCell>
                      <TableCell align="right">% проходження</TableCell>
                      <TableCell align="right">Середній %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byRole.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography color="text.secondary">Ще немає даних.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : byRole.map((row) => (
                      <TableRow key={row.role_name || row.role_id || 'none'} hover>
                        <TableCell>{row.role_name}</TableCell>
                        <TableCell align="right">{row.staff_count}</TableCell>
                        <TableCell align="right">{row.result_rows}</TableCell>
                        <TableCell align="right">{row.passed_rows}</TableCell>
                        <TableCell align="right">{row.pass_rate_pct}%</TableCell>
                        <TableCell align="right">{row.avg_pct}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}

          {(!manage || tab === 2) ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {manage ? 'Хто проходив' : 'Мої спроби'}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {manage ? <TableCell>Хто</TableCell> : null}
                      <TableCell>Тип</TableCell>
                      <TableCell>Назва</TableCell>
                      <TableCell>Результат</TableCell>
                      <TableCell>Дата</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activity.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={manage ? 5 : 4}>
                          <Typography color="text.secondary">Ще немає проходжень.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : activity.map((row) => (
                      <TableRow key={row.key} hover>
                        {manage ? <TableCell>{row.staff_name}</TableCell> : null}
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.kind === 'course' ? 'курс' : 'тест'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {row.title}
                          {row.detail ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              спроба {row.detail}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={row.result} color={row.resultColor} />
                        </TableCell>
                        <TableCell>{fmt(row.at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}
        </>
      )}
    </Box>
  );
}
